/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography, Typography2 } from '@coursera/cds-core';
import type { IconProps } from '@coursera/cds-core';
import {
  AchievementIcon,
  BriefcaseIcon,
  CheckIcon,
  DegreeIcon,
  HelpIcon,
  InstructorIcon,
  TargetIcon,
} from '@coursera/cds-icons';
import type { ViewForm as EventDataViewForm } from '@coursera/event-pulse-types';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { GoalTile } from 'bundles/enterprise-learner-onboarding/components/GoalTile';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { GoalNames } from 'bundles/enterprise-learner-onboarding/components/LearnerOnboardingContainer';
import type { SurveyQuestion } from 'bundles/enterprise-learner-onboarding/types/Survey';
import { Spacer } from 'bundles/enterprise-ui/components';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/program-home';

const tileContainer = css({
  display: 'flex',
  gap: '14px',
  flexWrap: 'wrap',
});

export type PropsFromCaller = {
  selectedGoals: GoalNames[];
  disabledGoals: GoalNames[];
  onGoalTileClick: (goal: GoalNames) => void;
  isProgramMember: boolean;
  surveyData?: SurveyQuestion;
  label: string;
  sharedEventingV3Data: Omit<EventDataViewForm, 'page' | 'user_properties'>;
};

export const SelectGoalsStep = ({
  selectedGoals,
  disabledGoals,
  onGoalTileClick,
  isProgramMember,
  surveyData,
  label,
  sharedEventingV3Data,
}: PropsFromCaller) => {
  const GoalMappings: {
    [K in (typeof GoalNames)[keyof typeof GoalNames]]: { icon: React.ComponentType<IconProps>; description: string };
  } = {
    [GoalNames.GROW]: {
      icon: AchievementIcon,
      description: _t('Develop skills for my current role'),
    },
    [GoalNames.SWITCH]: {
      icon: TargetIcon,
      description: _t('Expand my skills and career'),
    },
    [GoalNames.LAND]: {
      icon: BriefcaseIcon,
      description: _t('Kick-start my career'),
    },
    [GoalNames.CREDENTIALS]: {
      icon: DegreeIcon,
      description: _t('Earn a certificate or degree'),
    },
    [GoalNames.LEADER]: {
      icon: InstructorIcon,
      description: _t('Strengthen my management skills'),
    },
    [GoalNames.EXPLORE]: {
      icon: HelpIcon,
      description: _t('I just want to explore'),
    },
  };
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_form', {
    ...sharedEventingV3Data,
    form: { ...sharedEventingV3Data.form, fieldsAvailable: 4 },
  });

  return (
    <TrackedDiv
      trackingName="select_goals_step"
      data-testid="select-goals-step"
      withVisibilityTracking
      requireFullyVisible={false}
      trackClicks={false}
    >
      {/* Additional div as a workaround to get visibility tracking to work for eventing v3 */}
      <div ref={visibilityTrackingRef}>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography component="h1" variant="h1semibold">
          {label}
        </Typography>
        <Spacer size={12} />
        <Typography2 component="p" variant="bodyPrimary">
          {isProgramMember
            ? _t('Welcome back, select your goals — we’ll update your recommendations')
            : _t("Don't worry, you can always update this later in your profile")}
        </Typography2>
        <Spacer size={24} />
        <div css={tileContainer}>
          {surveyData?.options.map((goalOption) => (
            <GoalTile
              key={goalOption.id}
              title={GoalMappings[goalOption.id as GoalNames].description}
              name={goalOption.id as GoalNames}
              Icon={
                selectedGoals.includes(goalOption.id as GoalNames)
                  ? CheckIcon
                  : GoalMappings[goalOption.id as GoalNames].icon
              }
              isSelected={selectedGoals.includes(goalOption.id as GoalNames)}
              isDisabled={disabledGoals.includes(goalOption.id as GoalNames)}
              onClick={onGoalTileClick}
            />
          ))}
        </div>
        <Spacer size={48} />
      </div>
    </TrackedDiv>
  );
};
